import { DateTime } from 'luxon';

/**
 * Formats the time difference between the current time and a past timestamp
 * into a human-readable relative string (e.g., "2h 15m ago", "3d ago", etc.)
 *
 * @param timestamp - The past timestamp in seconds (Unix format)
 * @returns A relative time string representing how long ago the timestamp occurred
 */

export const formatTimeDifference = (timestamp: number): string => {
  const now = DateTime.now();
  const past = DateTime.fromSeconds(timestamp);

  const diff = now.diff(past, ['years', 'months', 'weeks', 'days', 'hours', 'minutes']).toObject();

  switch (true) {
    case !diff.minutes:
      return 'Now';
    case !diff.hours && diff.minutes && diff.minutes < 60:
      return `${Math.floor(diff.minutes!)}m ago`;
    case !diff.days && diff.hours && diff.hours < 24:
      return `${Math.floor(diff.hours!)}h ${Math.floor(diff.minutes! % 60)}m ago`;
    case !diff.weeks && diff.days && diff.days < 7:
      return `${Math.floor(diff.days!)}d ${Math.floor(diff.hours! % 24)}h ago`;
    case !diff.months && diff.weeks && diff.days! < 30:
      return `${Math.floor(diff.weeks!)}w ${Math.floor(diff.days! % 7)}d ${Math.floor(diff.hours! % 24)}h ago`;
    case !diff.years && diff.months && diff.months < 12:
      return `${Math.floor(diff.months!)}mo ${Math.floor(diff.days!)}d ago`;

    default:
      return `${Math.floor(diff.years!)}y ${Math.floor(diff.months!)}mo ${Math.floor(diff.days!)}d ago`;
  }
};

/**
 * Returns a string like "6 days ago @ 8/11/2024 - 9:14:23 AM"
 */
export const formatRelativeDateTime = (iso: string): string => {
  const dt = DateTime.fromISO(iso);

  if (!dt.isValid) return 'Invalid date';

  const relative = formatTimeDifference(dt.toSeconds());
  const absolute = dt.toFormat('M/d/yyyy - h:mm:ss a');

  return `${relative} @ ${absolute}`;
};
